import { useState, useCallback } from 'react'
import { httpsCallable } from 'firebase/functions'
import { useToast } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { VerifyOTPResponse } from '../../../types/EmailVerification'
import { OTP_LENGTH, MAX_ATTEMPTS } from '../../../constants/emailVerification'
import { getEffectiveServices } from '../../../utils/firebaseServiceContext'
import useStore from '../../../store/useStore'
import { auditBlockedImpersonationAction } from '../../../utils/impersonationAudit'

interface UseVerifyOTPReturn {
  verifyOTP: (otp: string) => Promise<void>
  loading: boolean
  error: string | null
  attemptsRemaining: number
  clearError: () => void
  resetOTP: () => void
}

/**
 * Hook for handling OTP verification logic.
 * Manages verification state, error handling, and navigation on success.
 */
export function useVerifyOTP(): UseVerifyOTPReturn {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attemptsRemaining, setAttemptsRemaining] = useState(MAX_ATTEMPTS)

  const toast = useToast()
  const navigate = useNavigate()
  const isImpersonating = useStore(
    (state) => state.identityStatus === 'impersonating',
  )

  const clearError = useCallback(() => setError(null), [])

  const resetOTP = useCallback(() => {
    setAttemptsRemaining(MAX_ATTEMPTS)
    setError(null)
  }, [])

  const verifyOTP = useCallback(
    async (otp: string) => {
      if (otp.length !== OTP_LENGTH) {
        setError(`Please enter a ${OTP_LENGTH}-digit code`)
        return
      }

      setLoading(true)
      setError(null)

      try {
        if (isImpersonating) {
          await auditBlockedImpersonationAction('account.otp-verify')
          setError('Email verification is disabled while acting as a user.')
          return
        }
        const functions = getEffectiveServices().functions
        const callable = httpsCallable<{ otp: string }, VerifyOTPResponse>(
          functions,
          'verifyOTPCallable'
        )

        const result = await callable({ otp })

        if (result.data.success) {
          toast({
            status: 'success',
            title: 'Email Verified',
            description: 'Your email has been verified successfully.',
            duration: 3000,
            isClosable: true,
          })
          navigate('/profile', { replace: true })
        } else {
          switch (result.data.error) {
            case 'invalid':
              setError('Invalid verification code. Please try again.')
              if (result.data.attemptsRemaining !== undefined) {
                setAttemptsRemaining(result.data.attemptsRemaining)
              }
              break
            case 'expired':
              setError(
                'Your verification code has expired. Please request a new one.'
              )
              break
            case 'max_attempts':
              setError('Too many failed attempts. Please request a new code.')
              setAttemptsRemaining(0)
              break
            default:
              setError('Verification failed. Please try again.')
          }
        }
      } catch (err: unknown) {
        console.error('Verification error:', err)
        setError('An error occurred. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [toast, navigate, isImpersonating]
  )

  return {
    verifyOTP,
    loading,
    error,
    attemptsRemaining,
    clearError,
    resetOTP,
  }
}
